import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getTrainingVideoLibrary, type GetTrainingVideoLibraryOutputType } from 'zite-endpoints-sdk';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { RefreshCw, ExternalLink, Play, AlertCircle, FileText, FileDown, Clock, Database } from 'lucide-react';

type Course = GetTrainingVideoLibraryOutputType['courses'][0];
type Module = Course['modules'][0];

function formatBytes(bytes: number): string {
  if (bytes === 0) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusBadge({ status, videoStatus }: { status: string; videoStatus?: string }) {
  const label = videoStatus || status;
  const map: Record<string, string> = {
    test: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
    draft_test: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
    in_production: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30',
    sop_ready: 'bg-blue-500/15 text-blue-500 border-blue-500/30',
    awaiting_script_approval: 'bg-purple-500/15 text-purple-500 border-purple-500/30',
  };
  const cls = map[label] || 'bg-muted text-muted-foreground';
  return <Badge className={`text-[10px] ${cls}`}>{label.replace(/_/g, ' ')}</Badge>;
}

function VideoPlayer({ mod }: { mod: Module }) {
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  if (!mod.videoUrl) return null;

  if (error) {
    return (
      <div className="aspect-video bg-muted rounded-md flex flex-col items-center justify-center gap-2 text-muted-foreground">
        <AlertCircle className="w-8 h-8" />
        <p className="text-sm">Video unavailable</p>
        <Button variant="outline" size="sm" onClick={() => { setError(false); setLoading(true); }}>
          <RefreshCw className="w-3 h-3 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="relative aspect-video bg-black rounded-md overflow-hidden">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted z-10">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <video
        key={mod.videoUrl}
        className="w-full h-full"
        controls
        preload="metadata"
        onLoadedData={() => setLoading(false)}
        onError={() => { setLoading(false); setError(true); }}
      >
        <source src={mod.videoUrl} type="video/mp4" />
      </video>
    </div>
  );
}

function ResourceLinks({ resources }: { resources: NonNullable<Module['resources']> }) {
  return (
    <div className="flex flex-wrap gap-2">
      {resources.map((r, i) => (
        <a
          key={i}
          href={r.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-muted/50 text-sm hover:bg-muted transition-colors"
        >
          {r.format === 'pdf' ? <FileText className="w-4 h-4 text-red-500" /> : <FileDown className="w-4 h-4 text-blue-500" />}
          {r.label}
          {r.bytes > 0 && <span className="text-xs text-muted-foreground">({formatBytes(r.bytes)})</span>}
        </a>
      ))}
    </div>
  );
}

function ModuleRow({ mod, isExpanded, onToggle }: { mod: Module; isExpanded: boolean; onToggle: () => void }) {
  const hasVideo = !!mod.videoUrl;
  const hasResources = mod.resources && mod.resources.length > 0;

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left"
      >
        <div className="flex items-center justify-center w-8 h-8 rounded-md bg-primary/10 text-primary text-xs font-bold shrink-0">
          {mod.code}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{mod.title}</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {mod.duration && <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{mod.duration}</span>}
            {mod.bytes > 0 && <span>· {formatBytes(mod.bytes)}</span>}
            {hasResources && !hasVideo && <span>· Documents available</span>}
          </div>
        </div>
        <StatusBadge status={mod.status} videoStatus={mod.videoStatus} />
        <Play className={`w-4 h-4 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          {hasVideo && <VideoPlayer mod={mod} />}
          {hasResources && <ResourceLinks resources={mod.resources!} />}
          <div className="flex items-center justify-between">
            {mod.sha256 && (
              <p className="text-xs text-muted-foreground font-mono truncate max-w-[60%]">
                SHA256: {mod.sha256.slice(0, 16)}…
              </p>
            )}
            {hasVideo && (
              <a
                href={mod.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                Open video <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CourseTab({ course }: { course: Course }) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <StatusBadge status={course.status} />
        {!course.approvedForMandatoryAssignment && (
          <Badge variant="outline" className="text-xs text-amber-600 border-amber-500/30">
            Not approved for assignment
          </Badge>
        )}
        <span className="text-xs text-muted-foreground ml-auto">{course.moduleCount} modules</span>
      </div>
      {course.description && (
        <p className="text-sm text-muted-foreground mb-3">{course.description}</p>
      )}
      {course.modules.map((mod, i) => (
        <ModuleRow
          key={`${course.code}-${mod.code}`}
          mod={mod}
          isExpanded={expandedIdx === i}
          onToggle={() => setExpandedIdx(expandedIdx === i ? null : i)}
        />
      ))}
    </div>
  );
}

const TAB_LABELS: Record<string, string> = {
  'RB-FOH-BAR-001': 'Bar',
  'RB-FOH-RESTAURANT-001': 'Restaurant',
  'RB-BMS-SCREEN-001': 'BMS Guides',
};

export default function TrainingVideoLibrary() {
  const { user, isLoading: authLoading, loginWithRedirect } = useAuth();
  const [data, setData] = useState<GetTrainingVideoLibraryOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && !user) loginWithRedirect();
  }, [authLoading, user, loginWithRedirect]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getTrainingVideoLibrary({});
      setData(result);
      if (!result.success && result.error) setError(result.error);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (authLoading || !user) return null;

  if (loading && !data) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-3xl space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full" />
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
      </div>
    );
  }

  if (error && !data?.courses?.length) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <AlertCircle className="w-12 h-12 text-destructive" />
          <h2 className="text-lg font-semibold">Catalogue Unavailable</h2>
          <p className="text-sm text-muted-foreground max-w-md">{error}</p>
          <Button onClick={load}><RefreshCw className="w-4 h-4 mr-2" /> Retry</Button>
        </div>
      </div>
    );
  }

  const courses = data?.courses || [];

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold">Learning Library</h1>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
            <Database className="w-3.5 h-3.5" />
            {data?.totalModules || courses.reduce((s, c) => s + c.moduleCount, 0)} modules across {courses.length} courses
            {data?.generatedAt && <span className="text-xs">· Catalogue {new Date(data.generatedAt).toLocaleDateString()}</span>}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {courses.length === 0 ? (
        <p className="text-muted-foreground text-center py-16">No courses found in catalogue.</p>
      ) : (
        <Tabs defaultValue={courses[0].code}>
          <TabsList className="w-full mb-4 flex-wrap h-auto gap-1">
            {courses.map(c => (
              <TabsTrigger key={c.code} value={c.code} className="flex-1 text-xs sm:text-sm min-w-[80px]">
                {TAB_LABELS[c.code] || c.title}
                <Badge variant="secondary" className="ml-1.5 text-[10px] px-1">{c.moduleCount}</Badge>
              </TabsTrigger>
            ))}
          </TabsList>
          {courses.map(c => (
            <TabsContent key={c.code} value={c.code}>
              <CourseTab course={c} />
            </TabsContent>
          ))}
        </Tabs>
      )}

      <p className="text-[11px] text-muted-foreground text-center mt-6">
        Source: phone.rbcomms.co.uk/assets/training/catalog.json · Read-only canonical catalogue
      </p>
    </div>
  );
}
